import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ActivityFeed,
  AlertPanel,
  KpiTile,
  PageHeader,
  SectionCard,
} from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type AnalyticsSummary = {
  kpis: {
    total_tenants: number;
    active_or_trial: number;
    active_only: number;
    trial_only: number;
    past_due: number;
    suspended: number;
    mrr_eur: number;
    sos_open: number;
    critical_anomalies: number;
    trial_expiring_7d: number;
    new_tenants_30d: number;
  };
  generated_at: string;
};

type AnalyticsVertical = {
  code: string;
  name: string;
  tenant_count: number;
  active_count: number;
  trial_count: number;
  mrr_eur: number;
  sos_open: number;
};

type AnalyticsActivity = {
  items: Array<{
    id: number;
    action: string;
    actor_sub: string | null;
    actor_role: string | null;
    store_id: number | null;
    details: Record<string, unknown> | null;
    created_at: string;
  }>;
  generated_at: string;
};

type AnalyticsAlert = {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  detail: string;
  store_id?: number;
  store_name?: string;
  created_at?: string;
};

const VERTICAL_ICONS: Record<string, string> = {
  restaurant: '🍽️',
  beauty: '💅',
  gym: '🏋️',
};

export function MasterHomePage() {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [verticals, setVerticals] = useState<AnalyticsVertical[]>([]);
  const [activity, setActivity] = useState<AnalyticsActivity['items']>([]);
  const [alerts, setAlerts] = useState<AnalyticsAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sumRes, vertRes, actRes, alertRes] = await Promise.all([
        apiFetch('/api/v1/master/analytics/summary'),
        apiFetch('/api/v1/master/analytics/verticals'),
        apiFetch('/api/v1/master/analytics/activity?limit=12'),
        apiFetch('/api/v1/master/analytics/alerts'),
      ]);
      if (sumRes.ok) {
        const body = (await sumRes.json().catch(() => null)) as AnalyticsSummary | null;
        if (body) setSummary(body);
      }
      if (vertRes.ok) {
        const body = (await vertRes.json().catch(() => null)) as { verticals?: AnalyticsVertical[] } | null;
        if (body?.verticals) setVerticals(body.verticals);
      }
      if (actRes.ok) {
        const body = (await actRes.json().catch(() => null)) as AnalyticsActivity | null;
        if (body?.items) setActivity(body.items);
      }
      if (alertRes.ok) {
        const body = (await alertRes.json().catch(() => null)) as { alerts?: AnalyticsAlert[] } | null;
        if (body?.alerts) setAlerts(body.alerts);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const k = summary?.kpis;
  const alertItems = alerts.map((a) => ({
    tone: (a.severity === 'critical' ? 'error' : a.severity === 'warning' ? 'warn' : 'info') as 'error' | 'warn' | 'info',
    title: a.title,
    detail: a.detail,
  }));

  const activityItems = activity.map((a) => ({
    id: a.id,
    action: a.action,
    actor: a.actor_sub,
    time: new Date(a.created_at).toLocaleString('it-IT'),
  }));

  return (
    <section className="sk-mc-page">
      <PageHeader
        title=""
        actions={
          <button type="button" className="sk-btn sk-btn--quiet" onClick={() => void load()}>
            Aggiorna
          </button>
        }
      />

      {error ? <p className="sk-error">{error}</p> : null}

      {/* KPI Row */}
      <div className="sk-mc-grid sk-mc-grid--kpi">
        <KpiTile
          label="MRR (attivi)"
          value={`€${(k?.mrr_eur || 0).toFixed(0)}/mese`}
          sub={loading ? 'Caricamento…' : `${k?.active_only || 0} attivi · ${k?.trial_only || 0} trial`}
          tone="ok"
          icon={<span>€</span>}
        />
        <KpiTile
          label="Tenant totali"
          value={k?.total_tenants ?? '—'}
          sub={loading ? 'Caricamento…' : `+${k?.new_tenants_30d || 0} ultimi 30gg`}
          tone="info"
          icon={<span>🏢</span>}
        />
        <KpiTile
          label="SOS aperti"
          value={k?.sos_open ?? 0}
          sub={k && k.sos_open > 0 ? 'Richiede attenzione' : 'Tutto OK'}
          tone={k && k.sos_open > 0 ? 'error' : 'ok'}
          icon={<span>🚨</span>}
        />
        <KpiTile
          label="Trial in scadenza"
          value={k?.trial_expiring_7d ?? 0}
          sub="Entro 7 giorni"
          tone={k && k.trial_expiring_7d > 0 ? 'warn' : 'neutral'}
          icon={<span>⏳</span>}
        />
        <KpiTile
          label="Past due / Sospesi"
          value={(k?.past_due || 0) + (k?.suspended || 0)}
          sub={k && (k.past_due + k.suspended) > 0 ? 'Follow-up needed' : 'Tutto OK'}
          tone={k && (k.past_due + k.suspended) > 0 ? 'warn' : 'ok'}
          icon={<span>⚠</span>}
        />
        <KpiTile
          label="Anomalie critiche"
          value={k?.critical_anomalies ?? 0}
          sub={k && k.critical_anomalies > 0 ? 'Stripe webhook' : 'Tutto OK'}
          tone={k && k.critical_anomalies > 0 ? 'error' : 'ok'}
          icon={<span>✕</span>}
        />
      </div>

      {/* Verticals breakdown */}
      {verticals.length > 0 ? (
        <SectionCard title="Verticali">
          <div className="sk-mc-grid sk-mc-grid--kpi">
            {verticals.map((v) => (
              <div key={v.code} className="sk-mc-vcard">
                <div className="sk-mc-vcard__icon">{VERTICAL_ICONS[v.code] || '📦'}</div>
                <div className="sk-mc-vcard__body">
                  <p className="sk-mc-vcard__name">{v.name}</p>
                  <p className="sk-mc-vcard__stats">
                    {v.tenant_count} tenant · {v.active_count} attivi · {v.trial_count} trial
                  </p>
                  <p className="sk-mc-vcard__mrr">€{v.mrr_eur.toFixed(0)}/mese</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      ) : null}

      {/* Two-column: Alerts + Activity */}
      <div className="sk-mc-grid sk-mc-grid--2col">
        <SectionCard title={`Alert (${alerts.length})`}>
          <AlertPanel alerts={alertItems} />
        </SectionCard>

        <SectionCard title="Attività recente">
          <ActivityFeed items={activityItems} />
        </SectionCard>
      </div>

      {/* Quick links */}
      <SectionCard title="Collegamenti rapidi">
        <div className="sk-mc-grid">
          <Link className="sk-box sk-box--primary sk-master-tile" to="/master/tenants">
            <p className="sk-box__label">Aziende</p>
            <h2 className="sk-box__title">Tenant</h2>
            <p className="sk-box__body">Elenco, dettaglio, moduli, billing.</p>
          </Link>
          <Link className="sk-box sk-box--info sk-master-tile" to="/master/plans">
            <p className="sk-box__label">Catalogo</p>
            <h2 className="sk-box__title">Piani & Moduli</h2>
            <p className="sk-box__body">CRUD piani, moduli, SMS packs.</p>
          </Link>
          <Link className="sk-box sk-box--primary sk-master-tile" to="/master/billing">
            <p className="sk-box__label">Billing</p>
            <h2 className="sk-box__title">Billing ops</h2>
            <p className="sk-box__body">Stripe, webhook, remediation.</p>
          </Link>
          <Link className="sk-box sk-box--primary sk-master-tile" to="/master/support/sos">
            <p className="sk-box__label">Supporto</p>
            <h2 className="sk-box__title">SOS emergenze</h2>
            <p className="sk-box__body">Ticket pubblici di emergenza.</p>
          </Link>
          <Link className="sk-box sk-box--info sk-master-tile" to="/master/audit">
            <p className="sk-box__label">Audit</p>
            <h2 className="sk-box__title">Audit log</h2>
            <p className="sk-box__body">Log azioni master globali.</p>
          </Link>
          <Link className="sk-box sk-box--neutral sk-master-tile" to="/master/email">
            <p className="sk-box__label">Config</p>
            <h2 className="sk-box__title">Sistema</h2>
            <p className="sk-box__body">Email, Twilio, encryption, reset.</p>
          </Link>
          <a className="sk-box sk-box--info sk-master-tile" href={import.meta.env.VITE_EAS_APP_URL || 'https://expo.dev/accounts/strikerposcloud.cloud/projects/striker-pass'} target="_blank" rel="noopener noreferrer">
            <p className="sk-box__label">App mobile</p>
            <h2 className="sk-box__title">StrikerPass</h2>
            <p className="sk-box__body">App end-user (EAS Build + OTA Update).</p>
          </a>
        </div>
      </SectionCard>
    </section>
  );
}
